import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import DashboardTable from "../../components/data_table/DashboardTable.tsx";
import {FlavorList} from "./Flavor.tsx";
import SnapshotList from "./Snapshot.tsx";
import {instance_manager} from "../../lib/InstanceManager.ts";
import {snapshot} from "../../lib/Snapshot.ts";
import type {CloudInit, Flavor, Instance, Keypair, Snapshot, UserInfoData} from "../../lib/ModelTypes.ts";
import {keypair} from "../../lib/Keypair.ts";
import {cloud_init_api} from "../../lib/CloudInit.ts";
import {frontBaseUrl} from "../../build_constants.ts";
import {Navbar} from "../../components/Navbar.tsx";
import {Trans, useTranslation} from "react-i18next";
import * as BsIcons from "react-icons/bs";
import AuthContext from "../../components/AuthContext.ts";

interface ContentProps {
    toggleSidebar: () => void;
}

const userInfoData = {
    id: "",
    email: "",
    signed: "",
    status: 0,
    expiration_date: "",
    use_start_date: "",
}

const Dashboard = ({toggleSidebar}: ContentProps) => {
    const {t} = useTranslation("ui");
    const navigate = useNavigate();

    const fetchCalledRef = useRef(false);
    const [userInfo, setUserInfo] = useState<UserInfoData>({...userInfoData});
    const [data, setData] = useState<{
        flavorData: Flavor[];
        snapshotData: Snapshot[];
        instanceData: Instance[];
        keyData: Keypair[];
        yamlData: CloudInit[]
    }>({
        flavorData: [],
        snapshotData: [],
        instanceData: [],
        keyData: [],
        yamlData: [],
    });
    const authContext = useContext(AuthContext);

    const init = useCallback(async () => {
        const userInfo = authContext!.userInfo;
        setUserInfo(userInfo);
        try {
            await instance_manager.prepare();
            setData(prev => ({...prev, flavorData: instance_manager.flavors}));

            await snapshot.getSnapshotList();
            setData(prev => ({...prev, snapshotData: snapshot.snapshots}));

            await instance_manager.getInstanceList();
            setData(prev => ({...prev, instanceData: instance_manager.instances}));

            const key = await keypair.get();
            setData(prev => ({...prev, keyData: key!.data}));

            const cloud_init = await cloud_init_api.get();
            if (cloud_init) {
                setData(prev => ({...prev, yamlData: cloud_init.data}));
            }
        } catch (err) {
            navigate(`${frontBaseUrl}/login`);
            console.error(err);
        }
    }, [authContext, navigate]);

    const content =
        userInfo.status === 0
            ? {
                icon: <BsIcons.BsClockHistory size={40} aria-hidden/>,
                title: "dashboard.pendingApproval",
                description: "dashboard.availableAfterApproval",
            }
            : userInfo.status === -1
                ? {
                    icon: <BsIcons.BsDisplay size={40} aria-hidden/>,
                    title: "dashboard.configuringServiceEnvironment",
                    description:
                        "dashboard.configuringServiceEnvironmentMsg",
                }
                : userInfo.status === -2
                    ? {
                        icon: <BsIcons.BsExclamationTriangle size={40} aria-hidden/>,
                        title: "dashboard.configuringServiceEnvironment",
                        description:
                            "dashboard.serviceEnvironmentConfigurationIsDelayed",
                    }
                    : {
                        icon: <BsIcons.BsXCircle size={40} aria-hidden />,
                        title: "dashboard.approvalDenied",
                        description: "dashboard.approvalDeniedMsg",
                    };

    useEffect(() => {
        if (fetchCalledRef.current) return;
        fetchCalledRef.current = true;
        init().finally();
    }, [init, fetchCalledRef]);

    return (
        <main className="d-block col ps-2 pt-3 border-0 min-vh-100">
            <Navbar toggleSidebar={toggleSidebar}/>

            <article className="blueshift_main" id="wrapper">
                {(userInfo.status === 2) ? <>
                        <div className="p-4 pt-2">
                            <h6 className="text-primary col-6">{t("dashboard.inUseInstances")}</h6>
                        </div>
                        <div className="row text-center align-items-center">
                            <FlavorList data={data.flavorData}/>
                        </div>
                        <div className="p-4 pt-4">
                            <h6 className="text-primary col-6">{t("dashboard.inUseSnapshots")}</h6>
                        </div>
                        <div className="row text-center align-items-center">
                            <SnapshotList data={data.snapshotData} instances={data.instanceData} flavor={data.flavorData}/>

                        </div>
                        <div className="p-4 pb-2">
                            <h6 className="text-primary col-6">{t("dashboard.otherResources")}</h6>
                        </div>
                        <div className="row px-5">
                            <div className="col-2">
                                <DashboardTable headers="Key-pair" items={data.keyData}/>
                            </div>
                            <div className="col-2">
                                <DashboardTable headers="YAML" items={data.yamlData}/>
                            </div>
                        </div>
                    </> :
                    <div
                        style={{
                            minHeight: "80vh",
                            display: "grid",
                            placeItems: "center",
                            textAlign: "center",
                        }}
                    >
                        <div style={{maxWidth: 520, padding: 24}}>
                            <div style={{marginBottom: 12}}>{content?.icon}</div>
                            <p style={{marginBottom: 8, fontSize: 25}}>{t(content?.title as string)}</p>
                            <div style={{color: "#6c757d", fontSize: 21, lineHeight: 1.6}}>
                                {content?.description && (
                                    <Trans
                                        ns="ui"
                                        i18nKey={content.description}
                                        components={{ br: <br /> }}
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                }
            </article>
        </main>
    );
}

export default Dashboard;
